import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BUILTIN_RECEIVER_URL,
  WEBHOOK_EVENTS,
  type EndpointInput,
  type WebhookEndpoint,
  type WebhookEvent,
} from "@/pages/admin/approvals/hooks/useWebhooks";

interface EndpointModalProps {
  open: boolean;
  initial: WebhookEndpoint | null;
  onClose: () => void;
  onSubmit: (input: EndpointInput) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

// Webhook 端点编辑弹窗：配置回调地址、签名密钥、订阅事件与启停。
export default function EndpointModal({ open, initial, onClose, onSubmit, notify }: EndpointModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setUrl(initial.url);
      setSecret(initial.secret ?? "");
      setEvents((initial.events as WebhookEvent[]).filter((event) => (WEBHOOK_EVENTS as string[]).includes(event)));
      setEnabled(initial.status === "active");
    } else {
      setName("");
      setUrl("");
      setSecret("");
      setEvents(["approval.completed"]);
      setEnabled(true);
    }
  }, [open, initial]);

  if (!open) return null;

  const toggleEvent = (event: WebhookEvent) => {
    setEvents((prev) => (prev.includes(event) ? prev.filter((item) => item !== event) : [...prev, event]));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      notify(t("approvals.endpointNameRequired"), "error");
      return;
    }
    if (!/^https?:\/\//i.test(url.trim())) {
      notify(t("approvals.endpointUrlRequired"), "error");
      return;
    }
    if (events.length === 0) {
      notify(t("approvals.endpointEventsRequired"), "error");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        endpointId: initial?.id,
        name: name.trim(),
        url: url.trim(),
        secret: secret.trim(),
        events,
        enabled,
      });
      notify(initial ? t("approvals.endpointUpdated") : t("approvals.endpointCreated"), "success");
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : t("approvals.saveFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-foreground-950/40 px-4 py-8">
      <div className="flex max-h-full w-full max-w-lg flex-col rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            {initial ? t("approvals.editEndpoint") : t("approvals.newEndpoint")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <label className="block text-xs font-medium text-foreground-700">{t("approvals.endpointName")}</label>
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("approvals.endpointNamePlaceholder")}
            className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />

          <label className="mt-3 block text-xs font-medium text-foreground-700">{t("approvals.endpointUrl")}</label>
          <input
            type="text"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://api.example.com/webhooks/licensehub"
            className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
          {BUILTIN_RECEIVER_URL && (
            <button
              type="button"
              onClick={() => setUrl(BUILTIN_RECEIVER_URL)}
              className="mt-1.5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-200 bg-secondary-50 px-2.5 py-1.5 text-[11px] font-medium text-secondary-900 transition-colors hover:bg-secondary-100"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-flask-line" />
              </span>
              {t("approvals.useBuiltinReceiver")}
            </button>
          )}
          <p className="mt-1 text-[11px] text-foreground-500">{t("approvals.builtinReceiverHint")}</p>

          <label className="mt-3 block text-xs font-medium text-foreground-700">{t("approvals.endpointSecret")}</label>
          <input
            type="text"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            placeholder={t("approvals.endpointSecretPlaceholder")}
            className="mt-1.5 h-10 w-full rounded-md border border-background-300 bg-background-50 px-3 font-mono text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          />
          <p className="mt-1 text-[11px] text-foreground-500">{t("approvals.endpointSecretHint")}</p>

          <label className="mt-3 block text-xs font-medium text-foreground-700">{t("approvals.endpointEvents")}</label>
          <div className="mt-1.5 flex flex-col gap-2">
            {WEBHOOK_EVENTS.map((event) => (
              <label
                key={event}
                className="flex cursor-pointer items-center gap-2.5 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
              >
                <input
                  type="checkbox"
                  checked={events.includes(event)}
                  onChange={() => toggleEvent(event)}
                  className="h-4 w-4 cursor-pointer accent-primary-600"
                />
                <span className="min-w-0">
                  <span className="block font-mono text-xs text-foreground-900">{event}</span>
                  <span className="block text-[11px] text-foreground-500">{t(`approvals.event_${event.replace(".", "_")}`)}</span>
                </span>
              </label>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setEnabled((value) => !value)}
            className={`mt-3 inline-flex w-full cursor-pointer items-center justify-between rounded-md border px-3 py-2.5 text-sm transition-colors ${
              enabled
                ? "border-primary-300 bg-primary-50 text-primary-700"
                : "border-background-300 bg-background-100 text-foreground-600"
            }`}
          >
            {enabled ? t("approvals.endpointEnabled") : t("approvals.endpointPaused")}
            <span className={`flex h-4 w-4 items-center justify-center ${enabled ? "text-primary-600" : "text-foreground-400"}`}>
              <i className={enabled ? "ri-toggle-fill" : "ri-toggle-line"} />
            </span>
          </button>
        </div>

        <div className="flex justify-end gap-2 border-t border-background-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("approvals.cancel")}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSubmit()}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
            </span>
            {saving ? t("approvals.saving") : t("approvals.save")}
          </button>
        </div>
      </div>
    </div>
  );
}